import React, { useEffect, useRef, useState } from "react";
import { Instagram, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { INSTA_REELS } from "@/data/products";

/**
 * Instagram Reels — luxury poster-thumbnail edition.
 * - Zero IG chrome (uses curated portrait jewelry posters, not iframes)
 * - Click → opens the live reel on instagram.com in a new tab
 * - Leading card auto-rotates every 6s (Vogue-style carousel)
 * - Idle Ken Burns zoom + shimmer + pulsing play overlay for that kinetic "reel feel"
 */

const ReelCard = ({ id, poster, caption, isLead }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <a
      href={`https://www.instagram.com/reel/${id}/`}
      target="_blank"
      rel="noopener noreferrer"
      className="reel-card group flex-shrink-0 w-[260px] sm:w-[300px] lg:w-[320px] relative block overflow-hidden rounded-[20px] bg-plum"
      data-testid={`reel-${id}`}
      style={{ aspectRatio: "9 / 16" }}
    >
      <div
        className={`absolute inset-0 reel-shimmer transition-opacity duration-700 ${
          loaded ? "opacity-0" : "opacity-100"
        }`}
        aria-hidden="true"
      />
      <img
        src={poster}
        alt={caption || "GirlyGrove reel"}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover ${
          isLead ? "reel-kenburns-lead" : "reel-kenburns"
        } group-hover:scale-110 transition-transform duration-[1200ms] ease-out`}
        draggable="false"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-plum/55 via-plum/0 to-plum/15 pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none transition-transform duration-300 group-hover:scale-110"
        aria-hidden="true"
      >
        <div className="reel-play-pulse w-16 h-16 rounded-full bg-cream/25 backdrop-blur-md flex items-center justify-center shadow-xl ring-1 ring-cream/50">
          <Play size={22} className="text-cream fill-cream ml-1" />
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
        <span className="text-cream/90 text-[11px] tracking-[0.22em] uppercase font-medium inline-flex items-center gap-2">
          <Instagram size={12} /> Watch reel
        </span>
        {caption && (
          <p className="text-cream text-[13px] mt-1.5 leading-snug font-light">
            {caption}
          </p>
        )}
      </div>
      <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-cream/15 backdrop-blur-md flex items-center justify-center ring-1 ring-cream/30 pointer-events-none">
        <Instagram size={13} className="text-cream" />
      </div>
    </a>
  );
};

const InstaReels = () => {
  const scrollRef = useRef(null);
  const [offset, setOffset] = useState(0);

  // Auto-rotate the leading reel every 6s
  useEffect(() => {
    const t = setInterval(() => {
      setOffset((v) => (v + 1) % INSTA_REELS.length);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const rotatedReels = [
    ...INSTA_REELS.slice(offset),
    ...INSTA_REELS.slice(0, offset),
  ];

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.85;
    scrollRef.current.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <section
      className="py-16 lg:py-24"
      style={{ background: "#FBEAF0" }}
      data-testid="insta-reels"
    >
      <style>{`
        @keyframes reelShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .reel-shimmer {
          background: linear-gradient(
            100deg,
            #F9F2EC 0%,
            #FBEAF0 25%,
            #F9D5C8 50%,
            #FBEAF0 75%,
            #F9F2EC 100%
          );
          background-size: 200% 100%;
          animation: reelShimmer 2.4s linear infinite;
        }

        @keyframes reelPulse {
          0%, 100% { transform: scale(1); opacity: 0.95; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        .reel-play-pulse { animation: reelPulse 2.4s ease-in-out infinite; }

        @keyframes kenBurns {
          0%   { transform: scale(1.05) translate(0, 0); }
          50%  { transform: scale(1.12) translate(-1.5%, -1%); }
          100% { transform: scale(1.05) translate(0, 0); }
        }
        .reel-kenburns      { animation: kenBurns 14s ease-in-out infinite; }
        .reel-kenburns-lead { animation: kenBurns 8s ease-in-out infinite; }

        @keyframes reelSlideIn {
          0%   { opacity: 0; transform: translateX(-30px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .reel-rotate-in { animation: reelSlideIn 0.7s cubic-bezier(0.2, 0.8, 0.2, 1); }
      `}</style>

      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="flex items-end justify-between gap-5 mb-10 lg:mb-12">
          <div>
            <p className="overline mb-3">Watch & Shop</p>
            <h2
              className="font-serif text-plum leading-[1] tracking-tight"
              style={{ fontSize: "clamp(32px, 4.4vw, 56px)" }}
            >
              On <span className="italic text-blush">reels</span>
            </h2>
            <p className="mt-3 text-plum/65 max-w-[480px] text-[15px]">
              Styling videos, behind the lens & how-to-wear edits — straight from our studio.
            </p>
          </div>
          <div className="hidden lg:flex items-center gap-2">
            <button
              onClick={() => scroll("left")}
              className="w-11 h-11 rounded-full border border-gold/40 text-plum flex items-center justify-center hover:bg-gold hover:text-cream hover:border-gold transition-colors"
              aria-label="Scroll left"
              data-testid="reels-scroll-left"
            >
              <ChevronLeft size={17} />
            </button>
            <button
              onClick={() => scroll("right")}
              className="w-11 h-11 rounded-full border border-gold/40 text-plum flex items-center justify-center hover:bg-gold hover:text-cream hover:border-gold transition-colors"
              aria-label="Scroll right"
              data-testid="reels-scroll-right"
            >
              <ChevronRight size={17} />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 lg:gap-5 overflow-x-auto no-scrollbar snap-x-mobile -mx-5 lg:mx-0 px-5 lg:px-0 pb-4"
        >
          {rotatedReels.map((reel, idx) => (
            <div
              key={`${reel.id}-${offset}-${idx}`}
              className={idx === 0 ? "reel-rotate-in" : ""}
            >
              <ReelCard
                id={reel.id}
                poster={reel.poster}
                caption={reel.caption}
                isLead={idx === 0}
              />
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <a
            href="https://www.instagram.com/girlygroveofficial/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline"
            data-testid="follow-instagram-reels-cta"
          >
            <Instagram size={15} /> Watch more on Instagram
          </a>
        </div>
      </div>
    </section>
  );
};

export default InstaReels;
